import { CalendarDate } from '@internationalized/date';
import {
  enumPaymentTerm,
  PaymentTerm,
} from '@rematter/frontend-lib-api-client';

import {
  parseCalendarDate,
  toISODateString,
} from '../../../shared/utils/dateTime';

/**
 * Calculates the due date based on the payment term and invoice/document date.
 * This mirrors the backend implementation
 */
export function calculateDueDate(
  paymentTerm: PaymentTerm,
  invoiceDate: CalendarDate,
): CalendarDate {
  switch (paymentTerm) {
    // Immediate payment terms - due on invoice date
    case enumPaymentTerm.CAD: // Cash Against Documents
    case enumPaymentTerm.CIA: // Cash In Advance
    case enumPaymentTerm.COD: // Cash On Delivery
    case enumPaymentTerm.PIA: // Payment In Advance
    case enumPaymentTerm.DueUponReceipt:
    case enumPaymentTerm.LOC: // Letter of Credit
      return invoiceDate;

    // Net terms - add specified number of days
    case enumPaymentTerm.Net5:
      return invoiceDate.add({ days: 5 });
    case enumPaymentTerm.Net10:
      return invoiceDate.add({ days: 10 });
    case enumPaymentTerm.Net15:
      return invoiceDate.add({ days: 15 });
    case enumPaymentTerm.Net20:
      return invoiceDate.add({ days: 20 });
    case enumPaymentTerm.Net30:
      return invoiceDate.add({ days: 30 });
    case enumPaymentTerm.Net45:
      return invoiceDate.add({ days: 45 });
    case enumPaymentTerm.Net60:
      return invoiceDate.add({ days: 60 });
    case enumPaymentTerm.Net90:
      return invoiceDate.add({ days: 90 });

    // End of month - last day of the current month
    case enumPaymentTerm.EOM: {
      // Get the first day of next month, then subtract one day
      const firstOfNextMonth = new CalendarDate(
        invoiceDate.year,
        invoiceDate.month + 1,
        1,
      );
      return firstOfNextMonth.subtract({ days: 1 });
    }

    // Cash Next Delivery - next day
    case enumPaymentTerm.CND:
      return invoiceDate.add({ days: 1 });

    // Default to invoice date for unknown terms
    default:
      return invoiceDate;
  }
}

/**
 * Calculates the due date from a string invoice date.
 */
export function calculateDueDateFromString(
  paymentTerm: PaymentTerm,
  invoiceDate: string,
): string {
  const calendarDate = parseCalendarDate(invoiceDate);
  const dueDate = calculateDueDate(paymentTerm, calendarDate);
  return toISODateString(dueDate);
}
